import {logError} from '../../../services/errors/errorReporting';
import {
  type CachedDataWithSize,
  type CacheTelemetry,
  type LongTermCache,
} from '../types';
import {logPerfAndErrors} from '../util';

/**
 * CacheApiCache is an implementation of the LongTermCache interface using the
 * Cache API available in modern browsers. This class provides methods to
 * interact with the browser's cache storage, allowing for storing, retrieving,
 * checking, and deleting cached data. It is designed to handle JSON-serializable
 * data and includes performance logging for cache operations.
 *
 * The Cache API is integrated with the browser's fetch API and stores cached
 * values as request/response pairs. More information can be found on MDN:
 * https://developer.mozilla.org/en-US/docs/Web/API/Cache
 */

type OperationContext = Record<string, unknown> & {
  outcome: 'hit' | 'miss' | 'stored' | 'disabled' | 'error';
  errorPhase?: string;
};

function describePayload<T>(
  context: OperationContext,
  data: T,
  telemetry?: CacheTelemetry<T>
) {
  try {
    Object.assign(context, telemetry?.describePayload?.(data));
  } catch {
    // Diagnostics must not change cache behavior.
    context.payloadDescriptionFailed = true;
  }
}

const CONTENT_LENGTH_HEADER = 'cache-content-length';

export class CacheApiCache implements LongTermCache {
  private openedCache: Cache | null = null;
  private browserHasCache = true;

  constructor(
    public readonly cacheName: string = 'wandb-graphql-cache',
    public readonly cacheVersion: string = 'v1'
  ) {}

  private disable() {
    this.browserHasCache = false;
    this.openedCache = null;
  }

  private async put(
    request: Request,
    response: Response,
    context: OperationContext
  ) {
    const cache = await this.ensurePrepared(context);
    if (cache == null) {
      return;
    }
    try {
      await cache.put(request, response);
      context.outcome = 'stored';
    } catch (error) {
      context.outcome = 'error';
      context.errorPhase = 'put';
      logError(`Error calling cache.put: ${error}, caching disabled`);
      this.disable();
    }
  }

  private async delete(request: Request) {
    const cache = await this.ensurePrepared();
    if (cache == null) {
      return;
    }
    try {
      await cache.delete(request);
    } catch (error) {
      logError(`Error calling cache.delete: ${error}, caching disabled`);
      this.disable();
    }
  }

  private async match(request: Request, context?: OperationContext) {
    const cache = await this.ensurePrepared(context);
    if (cache == null) {
      return null;
    }
    try {
      return await cache.match(request);
    } catch (error) {
      if (context) {
        context.outcome = 'error';
        context.errorPhase = 'match';
      }
      logError(`Error calling cache.match: ${error}, caching disabled`);
      this.disable();
      return null;
    }
  }

  async get<T>(
    key: string,
    namespace: string,
    telemetry?: CacheTelemetry<T>
  ): Promise<CachedDataWithSize<T> | null> {
    const context: OperationContext = {
      ...telemetry?.context,
      outcome: 'miss',
      serializedLengthUnit: 'utf16-code-units',
    };
    const cacheGet = async () => {
      let phaseStart = performance.now();
      const cache = await this.ensurePrepared(context);
      context.prepareMs = performance.now() - phaseStart;
      if (cache == null) {
        return null;
      }

      const request = new Request(key);
      phaseStart = performance.now();
      const cachedResponse = await this.match(request, context);
      context.matchMs = performance.now() - phaseStart;
      if (!cachedResponse) {
        return null;
      }

      const contentLength = cachedResponse.headers.get(CONTENT_LENGTH_HEADER);
      const size =
        contentLength && /^\d+$/.test(contentLength)
          ? Number(contentLength)
          : NaN;
      if (Number.isSafeInteger(size) && size >= 0) {
        context.serializedLengthCodeUnits = size;
      }
      let cachedData: T;
      phaseStart = performance.now();
      try {
        cachedData = await cachedResponse.json();
      } catch (error) {
        context.outcome = 'error';
        context.errorPhase = 'responseJson';
        throw error;
      } finally {
        // Includes body consumption and continuation scheduling, not just JSON parsing.
        context.responseJsonMs = performance.now() - phaseStart;
      }
      context.outcome = 'hit';
      describePayload(context, cachedData, telemetry);
      // Preserve the legacy return contract independently of the telemetry's strict header validation.
      const legacySize = contentLength ? parseInt(contentLength) : NaN;
      return cachedData
        ? {
            data: cachedData,
            bytesReadFromCache: isNaN(legacySize) ? 0 : legacySize,
          }
        : null;
    };
    const result = await logPerfAndErrors(
      'cache.get',
      cacheGet,
      namespace,
      context
    );
    return result ?? null;
  }

  async hasKey(key: string): Promise<boolean> {
    const cache = await this.ensurePrepared();
    if (cache == null) {
      return false;
    }
    const request = new Request(key);
    const match = await this.match(request);
    return match != null;
  }

  async set<T>(
    key: string,
    data: T,
    namespace: string,
    telemetry?: CacheTelemetry<T>
  ): Promise<void> {
    if (data == null) {
      return;
    }
    const context: OperationContext = {
      ...telemetry?.context,
      outcome: 'error',
      serializedLengthUnit: 'utf16-code-units',
    };
    const cacheSet = async () => {
      let phaseStart = performance.now();
      const cache = await this.ensurePrepared(context);
      context.prepareMs = performance.now() - phaseStart;
      if (cache == null) {
        return;
      }

      const request = new Request(key);
      let serializedData: string;
      phaseStart = performance.now();
      try {
        serializedData = JSON.stringify(data);
        context.serializedLengthCodeUnits = serializedData.length;
      } catch (error) {
        context.errorPhase = 'serialize';
        throw error;
      } finally {
        context.serializeMs = performance.now() - phaseStart;
      }
      let responseToCache: Response;
      phaseStart = performance.now();
      try {
        const headers = new Headers({
          [CONTENT_LENGTH_HEADER]: serializedData.length.toString(),
        });
        responseToCache = new Response(serializedData, {headers});
      } catch (error) {
        context.errorPhase = 'responseConstruction';
        throw error;
      } finally {
        context.responseConstructionMs = performance.now() - phaseStart;
      }
      phaseStart = performance.now();
      await this.put(request, responseToCache, context);
      context.putMs = performance.now() - phaseStart;
      describePayload(context, data, telemetry);
    };
    await logPerfAndErrors('cache.set', cacheSet, namespace, context);
  }

  async clear() {
    const cache = await this.ensurePrepared();
    if (cache == null) {
      return;
    }
    await caches.delete(this.getCacheHandle());
    this.openedCache = null;
  }

  async remove(key: string): Promise<void> {
    const cache = await this.ensurePrepared();
    if (cache == null) {
      return;
    }
    const request = new Request(key);
    await this.delete(request);
  }

  private getCacheHandle(): string {
    return `${this.cacheName}-${this.cacheVersion}`;
  }

  private async ensurePrepared(
    context?: OperationContext
  ): Promise<Cache | null> {
    if (!this.browserHasCache && context) {
      context.outcome = 'disabled';
    }
    if (this.openedCache == null && this.browserHasCache) {
      try {
        this.openedCache = await caches.open(this.getCacheHandle());
      } catch (error) {
        if (context) {
          context.outcome = 'error';
          context.errorPhase = 'prepare';
        }
        logError(`Error opening cache: ${error}, caching disabled`);
        this.browserHasCache = false;
      }
    }
    return this.openedCache;
  }
}
